import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from '@app/product/index.module.less';
import { Divider } from 'antd';
import { ServerProps } from 'base/interfaces/product';
import useAddCart from '@base/product/useAddCart';
import ProdSchema from '@base/product/ProdSchema';
import ProdBreadcrumbs from '@app/product/ProdBreadcrumbs';
import ProdBreadcrumbSchema from '@base/product/ProdBreadcrumbSchema';
import WebSiteSchema from '@base/common/WebSiteSchema';
import ProductTogether from '@app/product/ProductTogetherProducts';
import ProductMarkdownHtml from '@app/product/ProductMarkdownHtml';
import ProductDescription from '@app/product/ProductDescription';
import ProductReviewList from '@app/product/ProductReviewList';
import KitProducts from '@app/product/KitProducts';
import { hasSpec } from 'base/utils/product';
import ProdCompare from '@app/product/ProdCompare';
import ProductDetailInfo from '@app/product/ProductDetailInfo';
import ProdYouMayAlsoLike from '@app/product/ProdYouMayAlsoLike';
import FeaturesBanner from '@base/features/FeturesBanner';
import { useTrans } from 'base/utils/i18n';
import dynamic from 'next/dynamic';
import { IOldFeatureBanner } from 'mod/feature/interface';
import { EggHunt } from '@pro/common/EggHunt';
import CouponDrawWheel from '@base/activity/CouponDrawWheel';

const ProductDescriptionFeatures = dynamic(() => import('@app/product/ProductDescriptionFeatures'), { ssr: false });

type IProps = ServerProps

type ITabItem = {
    key: string;
    title: string | boolean;
    content: React.ReactElement;
    positionIndex?: number;      // 记录位置 (比较是否展示)
};

const ProductPage: Globals.NextPage<IProps> = props => {
    const bigPicRef = useRef<HTMLDivElement>(null);
    const [tabActiveIndex, setTabActiveIndex] = useState<number>(0)
    const { t } = useTrans();

    const { spu } = props;
    const { productId } = spu;

    const addCart = useAddCart({
        module: 'detail',
        spuInfo: spu,
        defaultThumbSize: 555,
        getThumbSrc: () => {
            if (bigPicRef && bigPicRef.current) {
                const firstImage = bigPicRef.current.querySelector('img');
                return firstImage?.currentSrc;
            }
        }
    });

    const { shownVariant, feature } = addCart;

    const markdownContent = shownVariant.descMarkdownHtml;
    const reviewInfo = shownVariant.review
    const hasKitProductVariantList = !!shownVariant.kitProductVariantList?.length

    const getTabList = (): ITabItem[] => {
        let list: ITabItem[] = [];
        if (markdownContent || shownVariant.descModuleCode) {
            list.push({
                key: 'desc',
                title: t('web_product_tab_description'),
                content: shownVariant.descModuleCode
                    ? <ProductDescriptionFeatures code={shownVariant.descModuleCode}/>
                    : <ProductMarkdownHtml content={markdownContent}/>
            });
        }

        // 有 bundle list 不展示 Technical Specs
        if (!hasKitProductVariantList && hasSpec(shownVariant)) {
            // 技术文档
            list.push({
                key: 'spec',
                title: t('web_product_tab_technical_specs'),
                content: <ProductDescription shownVariant={shownVariant}/>
            })
        }

        if (shownVariant) {
            // 商品对比
            list.push({
                key: 'prodCompare',
                title: false,  // 不展示tab title
                content: (
                    <ProdCompare
                        variantId={shownVariant.variantId as string}
                        variantTrackCode={shownVariant.variantTrackCode as string}
                    />
                )
            });
        }

        if (hasKitProductVariantList) {
            // bundle list
            list.push({
                key: 'bundle',
                title: t('web_product_tab_bundle_list'),
                content: <KitProducts shownVariant={shownVariant}/>
            })
        }

        if (reviewInfo && Number(reviewInfo.score) > 0) {
            // 评论
            list.push({
                key: 'review',
                title: t('web_product_tab_reviews', { num: reviewInfo.reviewNum }),
                content: (
                    <ProductReviewList
                        id='review-list'
                        variant={shownVariant}
                    />
                )
            });
        }
        // 记录位置
        list = list.map((item, index) => {
            return {
                ...item,
                positionIndex: index,
            }
        })

        return list;
    };

    const tabList = getTabList();

    useEffect(() => {
        setTabActiveIndex(0);
    }, [shownVariant])

    return (
        <>
            <WebSiteSchema/>
            <ProdBreadcrumbSchema variant={shownVariant} spuInfo={spu}/>
            <ProdSchema spuInfo={spu} reviewInfo={reviewInfo}/>

            <div className="wrapper">
                {/*面包屑导航*/}
                <ProdBreadcrumbs variant={shownVariant}/>

                {/*商品详情信息*/}
                <ProductDetailInfo
                    spuInfo={spu}
                    bigPicRef={bigPicRef}
                    reviewInfo={reviewInfo}
                    addCart={addCart}
                />

                {/* 搭配商品 */}
                <ProductTogether
                    productId={productId}
                    shownVariant={shownVariant}
                />
            </div>

            <Divider style={{ margin: '20px 0 9px' }}/>

            {/* feature banner */}
            {!!feature?.['product:feature:middle'] && (
                <FeaturesBanner
                    wrapClassName={'wrapper'}
                    dataList={feature['product:feature:middle'] as IOldFeatureBanner[]}
                    banner={{
                        imgShowMode: 'image',
                        navigation: false,
                        // pagination: false
                    }}
                />
            )}

            <div className={cn('wrapper flex', styles['tab-bar-nav'])}>
                {tabList?.filter(item => !!item.title)?.map((item) => (
                    <div
                        key={item.key}
                        onClick={() => setTabActiveIndex(item?.positionIndex as number || 0)}
                        className={cn(
                            'flex-1',
                            { [styles.active]: tabActiveIndex === item.positionIndex }
                        )}
                    >
                        <span>{item.title}</span>
                    </div>
                ))}
            </div>

            {tabList.map((item, index) => (
                <div
                    key={index}
                    className={cn(
                        styles['tab-part'],
                        { [styles['show-part']]: (tabActiveIndex <= (item?.positionIndex || 0)) }
                    )}
                >
                    <>{item.content}</>
                </div>
            ))}

            {/* you may also like */}
            <div className={'wrapper'}>
                <ProdYouMayAlsoLike variantId={shownVariant.variantId as string}/>
            </div>


            <CouponDrawWheel />

            <EggHunt id={shownVariant.variantId}/>
        </>
    )
};

export { getServerSideProps } from '@base/product/services';

ProductPage.pageConf = {
    pageCode: 'detail',
    bodyClassName: styles.body,
};

export default ProductPage;
